import {
  Delete,
  Download,
  Edit,
  Fullscreen,
  OpenInNew,
} from '@mui/icons-material';
import ShareIcon from '@mui/icons-material/Share';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ConvertIcon } from '@work4all/assets/icons/convert.svg';
import { ReactComponent as EditCopyIcon } from '@work4all/assets/icons/edit-pdf.svg';

import { usePermissions } from '@work4all/data';
import { useAuthHeaders } from '@work4all/data/lib/auth/use-auth-headers';
import { usePopoverState } from '@work4all/data/lib/hooks/usePopoverState';
import {
  downloadAuthed,
  parseNameAndExtension,
} from '@work4all/data/lib/utils';

import { useToolbarCustomActionsComponents } from '../../../../dataDisplay/actions-bar/hooks/use-toolbar-custom-actions';
import { CustomToolbarMappedAction } from '../../../../dataDisplay/actions-bar/ToolbarTypes';
import { MIME_TYPES } from '../../../../preview/Preview';
import { ConvertPopover } from '../../../convert-popover/ConvertPopover';
import { PreviewTitle } from '../../components';
import { PreviewTitleInput } from '../../components/preview-input';
import { useErpFileName } from '../../erp-preview/hooks/use-erp-file-name';
import { IDocumentPreviewProps } from '../types/IDocumentPreviewProps';

import { DocumentEditablePreview } from './DocumentEditablePreview';

interface DocumentPreviewTitleProps extends IDocumentPreviewProps {
  setFullscreen: (value: boolean) => void;
  isLinkable: boolean;
  onTitleChanged?: (value: string | number) => void;
  editPdfTitle?: string;
  editPdfDisabled?: boolean;
}

export const DocumentPreviewTitle = (props: DocumentPreviewTitleProps) => {
  const {
    title,
    downloadUrl,
    filePath,
    mimeType,
    onCloseClick,
    onEditClicked,
    onDeleteClicked,
    onPdfEditClicked,
    fspUrl,
    exists,
    navigation,
    iconProps,
    onShareClicked,
    openInFullscreen,
    erpPreview,
    showFullscreenButton = true,
    showDownloadButton = true,
    customActions: custom,
    additionalTitleItems,
    convertProps,
    selectedRowsIdsList,
    entries = [],
    entity,
    entityData,
    setFullscreen,
    titleKeyField,
    isLinkable,
    mutate,
    onVisibilityToggle,
    onTitleChanged,
    multiline,
    contractNumber,
    number,
    value,
    language,
    editPdfTitle,
    editPdfDisabled,
  } = props;

  const convertPopoverState = usePopoverState('bottom');

  const mimeTypeValues = Object.values(MIME_TYPES);

  const canPreview =
    showFullscreenButton && mimeTypeValues.includes(mimeType) ? true : false;

  const canDownload = showDownloadButton ? true : false;

  useEffect(() => {
    if (openInFullscreen) {
      setFullscreen(true);
    }
  }, [openInFullscreen, setFullscreen]);

  const toolbar = useToolbarCustomActionsComponents({
    custom,
    skipMode: true,
    selectedRowsIdsList,
  });

  const { t } = useTranslation();
  const httpHeaders = useAuthHeaders();

  const { canEdit: canEditChecker } = usePermissions();

  const canEdit = entity && canEditChecker({ entity, record: entityData });

  const fileName = useErpFileName({
    entityType: entity,
    data: {
      contractNumber,
      number,
      value,
      language: {
        id: language?.id || 0,
      },
    },
  });

  const customFirstLevelActions: CustomToolbarMappedAction[] = [
    ...(onEditClicked
      ? [
          {
            title: t('COMMON.EDIT'),
            actionKey: 'edit-copy',
            disabled: !canEdit,
            disableReason: !canEdit ? t('RIGHTS.MISSING') : undefined,
            IconComponent: Edit,
            onClick: onEditClicked,
          },
        ]
      : []),
  ];

  const customActions: CustomToolbarMappedAction[] = [
    ...(onPdfEditClicked
      ? [
          {
            title: editPdfTitle ?? t('COMMON.EDIT_COPY'),
            actionKey: 'edit-copy',
            IconComponent: EditCopyIcon,
            onClick: onPdfEditClicked,
            disabled: editPdfDisabled,
          },
        ]
      : []),
    ...toolbar.left,
    ...(onShareClicked
      ? [
          {
            title: t('COMMON.SHARE'),
            actionKey: 'share',
            IconComponent: ShareIcon,
            onClick: onShareClicked,
          },
        ]
      : []),
    ...(onDeleteClicked
      ? [
          {
            title: t('COMMON.DELETE'),
            actionKey: 'delete',
            IconComponent: Delete,
            onClick: onDeleteClicked,
          },
        ]
      : []),
    ...(convertProps
      ? [
          {
            title: t('COMMON.CONVERT'),
            actionKey: 'convert',
            IconComponent: ConvertIcon,
            onClick: convertPopoverState.handleClick,
          },
        ]
      : []),
    ...(canPreview
      ? [
          {
            title: t('COMMON.FULLSCREEN'),
            actionKey: 'fullscreen',
            IconComponent: Fullscreen,
            onClick: () => setFullscreen(true),
          },
        ]
      : []),
    ...(!erpPreview
      ? canDownload && !canPreview && exists
        ? [
            {
              title: t('COMMON.DOWNLOAD'),
              actionKey: 'download',
              IconComponent: Download,
              href: fspUrl,
              download: true,
            },
          ]
        : []
      : canDownload && downloadUrl
      ? [
          {
            title: t('COMMON.DOWNLOAD'),
            actionKey: 'download',
            IconComponent: Download,
            onClick: (e) => {
              e.preventDefault();
              downloadAuthed(
                downloadUrl,
                `${isLinkable ? filePath : fileName.split('.pdf')[0]}.${
                  parseNameAndExtension(filePath).extension
                }`,
                httpHeaders
              );
            },
          },
        ]
      : []),
    ...(iconProps?.showPreviewExternallyIcon !== false &&
    ((filePath && isLinkable) || fspUrl)
      ? [
          {
            title: t('MASK.OPEN_IN_NEW_TAB'),
            actionKey: 'open-in-new-tab',
            IconComponent: OpenInNew,
            href: filePath && isLinkable ? filePath : fspUrl,
            target: '_blank',
          },
        ]
      : []),
    ...toolbar.right,
  ];

  const previewTitle = entity ? (
    <DocumentEditablePreview
      entity={entity}
      entityData={entityData}
      entries={entries}
    >
      {({ onEdit, onPopoverClose }) => (
        <PreviewTitleInput
          onClose={(e) => {
            if (e.target.value !== title) {
              if (mutate) mutate({ [titleKeyField]: e.target.value });
              else onEdit({ [titleKeyField]: e.target.value });
            }
            onPopoverClose();
          }}
          value={title}
          onChange={onTitleChanged}
          onCloseClick={onCloseClick}
          accessor={titleKeyField}
          customFirstLevelActions={customFirstLevelActions}
          customPreviewActions={customActions}
          leftActions={
            <>
              {additionalTitleItems}
              {navigation ? navigation : null}
            </>
          }
          onVisibilityToggle={onVisibilityToggle}
          multiline={multiline}
        />
      )}
    </DocumentEditablePreview>
  ) : (
    <PreviewTitle
      onCloseClick={(e) => {
        setFullscreen(false);
        //eslint-disable-next-line
        //@ts-expect-error type not assignable to parameter 'MouseEvent<HTMLButtonElement, MouseEvent>'
        onCloseClick(e);
      }}
      onShareClicked={onShareClicked}
      leftActions={navigation ? navigation : null}
      customFirstLevelActions={customFirstLevelActions}
      customPreviewActions={customActions}
      onVisibilityToggle={onVisibilityToggle}
    >
      {title}
    </PreviewTitle>
  );

  return (
    <>
      {convertProps && (
        <ConvertPopover {...convertProps} popoverState={convertPopoverState} />
      )}
      {previewTitle}
    </>
  );
};
